import { useState, useRef, useCallback, useEffect } from 'react';
import { PCMAudioPlayer } from '@/utils/audioPlayer';
import { streamMessage } from '@/services/interview';
import type { MessageStreamEvent } from '@/types';

/** 采样率，与后端下行音频格式一致 */
const PCM_SAMPLE_RATE = 24000;

/** TTS 流状态枚举 */
export enum TTSStatus {
  /** 未连接 */
  Idle = 'idle',
  /** 连接中 */
  Connecting = 'connecting',
  /** 播放中 */
  Playing = 'playing',
  /** 已完成 */
  Completed = 'completed',
  /** 错误 */
  Error = 'error',
  /** 已断开（用户或组件卸载中止） */
  Aborted = 'aborted',
  /** 连接失败 */
  ConnectFailed = 'connect_failed',
}

export interface TTSStreamResult {
  interviewNumber: number;
  messageId: string;
  content: string;
  isFinal: boolean;
  timestamp: number;
}

export interface TTSConfig {
  /** 流式文本回调，每次按 sequence 拼接后的完整文案 */
  onStream?: (result: TTSStreamResult) => void;
  onError?: (error: Error) => void;
}

export interface UseTTSReturn {
  /** 是否正在拉流（加载态） */
  loading: boolean;
  /** 是否正在播放 */
  playing: boolean;
  status: TTSStatus;
  error: string | null;
  /** 连接并消费指定消息的流（文本 + 音频） */
  connect: (interviewNumber: number, messageId: string) => Promise<void>;
  /** 仅跳过当前音频播放，不影响文本逻辑 */
  skip: () => void;
  /** 断开并释放资源 */
  destory: () => void;
}

/** 从 Map<sequence, content> 按 sequence 从 0 起连续拼接为完整文案 */
function getOrderedText(textBySequence: Map<number, string>): string {
  const parts: string[] = [];
  let i = 0;
  while (textBySequence.has(i)) {
    parts.push(textBySequence.get(i)!);
    i++;
  }
  return parts.join('');
}

/** 解析 SSE 行，提取 data 行中的 JSON */
function parseSSELine(line: string): MessageStreamEvent | null {
  const trimmed = line.trim();
  if (trimmed.startsWith('data:')) {
    const payload = trimmed.slice(5).trim();
    if (payload === '[DONE]' || payload === '') return null;
    try {
      return JSON.parse(payload) as MessageStreamEvent;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * 面试下行流式 TTS Hook
 * 基于 POST /api/interviews/message/stream 的 SSE 流，按 sequence 拼接文本并播放 PCM 音频
 */
export function useTTS(config: TTSConfig = {}): UseTTSReturn {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [status, setStatus] = useState<TTSStatus>(TTSStatus.Idle);
  const [error, setError] = useState<string | null>(null);

  const playerRef = useRef<PCMAudioPlayer | null>(null);
  const playingRef = useRef<number | null>(null);
  const streamEndedRef = useRef<boolean>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const textBySequenceRef = useRef<Map<number, string>>(new Map());
  const onStreamRef = useRef<((result: TTSStreamResult) => void) | undefined>();
  const onErrorRef = useRef<((error: Error) => void) | undefined>();

  useEffect(() => {
    onStreamRef.current = config.onStream;
    onErrorRef.current = config.onError;
  }, [config.onStream, config.onError]);

  const skip = useCallback(() => {
    streamEndedRef.current = true;
    if (playingRef.current) {
      clearTimeout(playingRef.current);
      playingRef.current = null;
    }
    if (playerRef.current) {
      playerRef.current.stop();
    }
    setLoading(false);
    setPlaying(false);
  }, []);

  const destory = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
    if (playingRef.current) {
      clearTimeout(playingRef.current);
      playingRef.current = null;
    }
    textBySequenceRef.current.clear();
    setPlaying(false);
    setLoading(false);
    setStatus(TTSStatus.Idle);
  }, []);

  const connect = useCallback(async (interviewNumber: number, messageId: string) => {
    if (!interviewNumber || !messageId) {
      const err = new Error('interviewNumber 和 messageId 为必填');
      setError(err.message);
      onErrorRef.current?.(err);
      return;
    }

    // 终止上一次流（若有）
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    textBySequenceRef.current.clear();
    setError(null);
    setStatus(TTSStatus.Connecting);

    // 复用或新建播放器
    if (!playerRef.current) {
      playerRef.current = new PCMAudioPlayer(PCM_SAMPLE_RATE);
      playerRef.current.connect();
    }

    let response: Response;
    try {
      response = await streamMessage(interviewNumber, messageId, {
        signal: abortControllerRef.current.signal,
      });
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err.message);
      setStatus(TTSStatus.ConnectFailed);
      onErrorRef.current?.(err);
      return;
    }

    if (!response.ok) {
      const err = new Error(`流式请求失败: ${response.status} ${response.statusText}`);
      setError(err.message);
      setStatus(TTSStatus.ConnectFailed);
      onErrorRef.current?.(err);
      return;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('text/event-stream')) {
      const err = new Error('响应不是 text/event-stream');
      setError(err.message);
      onErrorRef.current?.(err);
      return;
    }

    const body = response.body;
    if (!body) {
      const err = new Error('无响应体');
      setError(err.message);
      onErrorRef.current?.(err);
      return;
    }

    setLoading(true);
    setStatus(TTSStatus.Playing);

    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    streamEndedRef.current = false;

    try {
      while (!streamEndedRef.current) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const event = parseSSELine(line);
          if (!event) continue;

          if (event.type === 'TEXT') {
            textBySequenceRef.current.set(event.sequence, event.content);
            const fullText = getOrderedText(textBySequenceRef.current);
            onStreamRef.current?.({
              interviewNumber,
              messageId,
              content: fullText,
              timestamp: event.timestamp,
              isFinal: false,
            });
          } else if (event.type === 'AUDIO') {
            try {
              const binaryString = atob(event.content);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const arrayBuffer = bytes.buffer;
              if (playerRef.current) {
                playerRef.current.pushPCM(arrayBuffer);
              }
            } catch (e) {
              const decodeErr =
                e instanceof Error ? e : new Error(`音频 base64 解码失败: ${String(e)}`);
              onErrorRef.current?.(decodeErr);
            } finally {
              setPlaying(true);
            }
          } else if (event.type === 'END') {
            setStatus(TTSStatus.Completed);
            if (event.messageType === 'TEXT_DONE') {
              const fullText = getOrderedText(textBySequenceRef.current);
              onStreamRef.current?.({
                interviewNumber,
                messageId,
                content: fullText,
                timestamp: event.timestamp,
                isFinal: true,
              });
            }
            if (event.messageType === 'AUDIO_DONE') {
              streamEndedRef.current = true;
              const { duration = 0 } = JSON.parse(event.content);
              playingRef.current = setTimeout(() => {
                setPlaying(false);
              }, duration);
            }
            break;
          } else if (event.type === 'ERROR') {
            const err = new Error(event.content ?? event.code ?? '流式推送错误');
            setError(err.message);
            onErrorRef.current?.(err);
            setStatus(TTSStatus.Error);
            streamEndedRef.current = true;
            break;
          }
        }
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        setStatus(TTSStatus.Aborted);
      } else {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err.message);
        onErrorRef.current?.(err);
        setStatus(TTSStatus.Error);
      }
    } finally {
      setLoading(false);
      reader.releaseLock();
    }
  }, []);

  useEffect(() => {
    return () => {
      destory();
    };
  }, [destory]);

  return {
    playing,
    loading,
    status,
    error,
    connect,
    skip,
    destory,
  };
}
